import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/auth';

const CONTRACT_TYPES = ['Permanent', 'Vacataire', 'Contractuel', 'Stagiaire'];
const LEVELS = ['6ème', '5ème', '4ème', '3ème', 'Seconde', 'Première', 'Terminale'];

const Teachers = () => {
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(1);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSpeciality, setFilterSpeciality] = useState('all');

  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);

  const [newAssignment, setNewAssignment] = useState({
    class_id: '',
    subject_id: '',
    role: 'Enseignant',
  });

  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    birth_date: '',
    sexe: '',
    phone: '',
    address: '',
    matricule: '',
    speciality: '',
    diploma: '',
    hire_date: new Date().toISOString().split('T')[0],
    contract_type: 'Permanent',
    is_active: true,
    assignments: [],
    class_teacher_of: [],
    email: '',
    password: '',
    password_confirmation: '',
    send_credentials: true,
  });

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const showToast = (message, type = 'success') => setToast({ message, type });

  useEffect(() => {
    fetchTeachers();
    fetchClasses();
    fetchSubjects();
  }, []);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get('/teachers', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeachers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Erreur profs:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/classes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClasses(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Erreur classes:', e);
    }
  };

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/subjects', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubjects(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Erreur subjects:', e);
    }
  };

  const filteredTeachers = useMemo(() => {
    return teachers.filter((t) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const name = (t.name || '').toLowerCase();
        const mat = (t.matricule || '').toLowerCase();
        if (!name.includes(term) && !mat.includes(term)) return false;
      }
      if (filterSpeciality !== 'all' && t.speciality !== filterSpeciality) return false;
      return true;
    });
  }, [teachers, searchTerm, filterSpeciality]);

  const specialities = useMemo(() => {
    const set = new Set(teachers.map((t) => t.speciality).filter(Boolean));
    return Array.from(set).sort();
  }, [teachers]);

  const handleAddAssignment = () => {
    if (!newAssignment.class_id || !newAssignment.subject_id) {
      showToast('Sélectionnez une classe et une matière', 'error');
      return;
    }

    const exists = formData.assignments.some(
      (a) =>
        String(a.class_id) === String(newAssignment.class_id) &&
        String(a.subject_id) === String(newAssignment.subject_id)
    );

    if (exists) {
      showToast('Cette affectation existe déjà', 'error');
      return;
    }

    const classObj = classes.find((c) => String(c.id) === String(newAssignment.class_id));
    const subjectObj = subjects.find((s) => String(s.id) === String(newAssignment.subject_id));

    setFormData({
      ...formData,
      assignments: [
        ...formData.assignments,
        {
          class_id: newAssignment.class_id,
          class_name: classObj?.name || '',
          subject_id: newAssignment.subject_id,
          subject_name: subjectObj?.name || '',
          role: newAssignment.role,
        },
      ],
    });

    setNewAssignment({ class_id: '', subject_id: '', role: 'Enseignant' });
  };

  const handleRemoveAssignment = (index) => {
    const newAssignments = formData.assignments.filter((_, i) => i !== index);
    setFormData({ ...formData, assignments: newAssignments });
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const fullName = `${formData.lastName} ${formData.firstName}`.trim();

      const payload = {
        name: fullName,
        email: formData.email || null,
        phone: formData.phone,
        birth_date: formData.birth_date || null,
        sexe: formData.sexe,
        address: formData.address,
        matricule: formData.matricule || null,
        speciality: formData.speciality,
        diploma: formData.diploma,
        hire_date: formData.hire_date || null,
        contract_type: formData.contract_type,
        is_active: formData.is_active,
        password: formData.password || null,
        assignments: formData.assignments.map((a) => ({
          class_id: a.class_id,
          subject_id: a.subject_id,
        })),
        class_teacher_of: formData.class_teacher_of,
      };

      if (editingTeacher) {
        await api.put(`/teachers/${editingTeacher.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showToast('Professeur modifié avec succès !', 'success');
        closeModal();
        fetchTeachers();
      } else {
        const res = await api.post('/teachers', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setCreatedCredentials({
          name: fullName,
          email: res.data?.generated_email || res.data?.data?.email,
          password: res.data?.generated_password || formData.password || 'password123',
          matricule: res.data?.data?.matricule,
          speciality: formData.speciality,
          contract_type: formData.contract_type,
          assignments_count: res.data?.assignments_count || 0,
        });
        setShowCredentialsModal(true);
        fetchTeachers();
      }
    } catch (error) {
      console.error('Erreur:', error);
      const msg =
        error.response?.data?.error ||
        (typeof error.response?.data?.errors === 'object'
          ? Object.values(error.response.data.errors).flat().join(', ')
          : "Erreur lors de l'enregistrement");
      showToast(msg, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce professeur ?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/teachers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Professeur supprimé avec succès !', 'success');
      fetchTeachers();
    } catch (error) {
      showToast('Erreur lors de la suppression', 'error');
    }
  };

  const handleEdit = async (teacher) => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/teachers/${teacher.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { user, assignments, class_teacher_of } = res.data;
      const nameParts = (user.name || '').split(' ');
      const lastName = nameParts[0] || '';
      const firstName = nameParts.slice(1).join(' ') || '';

      setEditingTeacher(teacher);
      setFormData({
        lastName,
        firstName,
        birth_date: user.birth_date || '',
        sexe: user.sexe || '',
        phone: user.phone || '',
        address: user.address || '',
        matricule: user.matricule || '',
        speciality: user.speciality || '',
        diploma: user.diploma || '',
        hire_date: user.hire_date || '',
        contract_type: user.contract_type || 'Permanent',
        is_active: user.is_active !== false,
        assignments: assignments.map((a) => ({
          class_id: a.class_id,
          class_name: a.class_name,
          subject_id: a.subject_id,
          subject_name: a.subject_name,
          role: 'Enseignant',
        })),
        class_teacher_of: (class_teacher_of || []).map((c) => String(c.id)),
        email: user.email || '',
        password: '',
        password_confirmation: '',
        send_credentials: false,
      });
      setStep(1);
      setShowModal(true);
    } catch (error) {
      showToast('Erreur lors du chargement du professeur', 'error');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTeacher(null);
    setStep(1);
    setNewAssignment({ class_id: '', subject_id: '', role: 'Enseignant' });
    setFormData({
      lastName: '',
      firstName: '',
      birth_date: '',
      sexe: '',
      phone: '',
      address: '',
      matricule: '',
      speciality: '',
      diploma: '',
      hire_date: new Date().toISOString().split('T')[0],
      contract_type: 'Permanent',
      is_active: true,
      assignments: [],
      class_teacher_of: [],
      email: '',
      password: '',
      password_confirmation: '',
      send_credentials: true,
    });
  };

  const handleNew = () => {
    setEditingTeacher(null);
    setStep(1);
    setShowModal(true);
  };

  const canGoNext = () => {
    if (step === 1) return formData.lastName && formData.firstName && formData.phone;
    if (step === 2) return formData.speciality;
    if (step === 3) return true;
    return true;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  };

  const handlePrint = () => {
    if (!createdCredentials) return;
    const c = createdCredentials;

    const w = window.open('', '_blank');
    const html = `
      <html>
        <head>
          <title>Identifiants - ${c.name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .fiche { border: 2px solid #333; padding: 20px; margin-bottom: 20px; max-width: 500px; }
            .fiche h2 { margin: 0 0 10px 0; color: #E67E22; }
            .row { display: flex; margin: 6px 0; }
            .label { font-weight: bold; width: 140px; }
            .value { font-family: monospace; }
            .note { background: #FFF3CD; padding: 10px; margin-top: 15px; font-size: 12px; border-left: 4px solid #E67E22; }
            @media print { body { padding: 0; } .fiche { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <div class="fiche">
            <h2>🎓 SamaEcole - Fiche Professeur</h2>
            <div class="row"><span class="label">Nom :</span><span class="value">${c.name}</span></div>
            ${c.matricule ? `<div class="row"><span class="label">Matricule :</span><span class="value">${c.matricule}</span></div>` : ''}
            ${c.speciality ? `<div class="row"><span class="label">Spécialité :</span><span class="value">${c.speciality}</span></div>` : ''}
            ${c.contract_type ? `<div class="row"><span class="label">Contrat :</span><span class="value">${c.contract_type}</span></div>` : ''}
            <div class="row"><span class="label">📧 Email :</span><span class="value">${c.email}</span></div>
            <div class="row"><span class="label">🔑 Mot de passe :</span><span class="value">${c.password}</span></div>
            <div class="note">⚠️ Changez votre mot de passe à la 1ère connexion sur : http://samaecole.sn/login</div>
          </div>
          <script>window.print();</script>
        </body>
      </html>
    `;
    w.document.write(html);
    w.document.close();
  };

  const handleCopy = () => {
    if (!createdCredentials) return;
    const c = createdCredentials;

    const text = `=== PROFESSEUR ===\nNom: ${c.name}\nEmail: ${c.email}\nMot de passe: ${c.password}`;
    navigator.clipboard.writeText(text);
    showToast('Identifiants copiés !', 'success');
  };

  return (
    <div className="p-6">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[100] ${
            toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 min-w-[280px]`}
        >
          <span className="text-xl">{toast.type === 'success' ? '✅' : '❌'}</span>
          <span className="flex-1 font-medium text-sm">{toast.message}</span>
          <button onClick={() => setToast(null)} className="text-white text-2xl font-bold leading-none">×</button>
        </div>
      )}

      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">👨‍🏫 Gestion des professeurs</h1>
          <p className="text-gray-500">Créez et gérez les professeurs de votre établissement</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={handleNew} className="bg-[#E67E22] text-white px-4 py-2 rounded-lg hover:bg-[#D35400] transition font-medium">
            ➕ Créer un professeur
          </button>
          <a
            href="/admin/teachers/import"
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition font-medium flex items-center gap-2"
          >
            📥 Importer Excel
          </a>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <input
          type="text"
          placeholder="🔍 Rechercher par nom ou matricule..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
        />
        <select
          value={filterSpeciality}
          onChange={(e) => setFilterSpeciality(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
        >
          <option value="all">Toutes les spécialités</option>
          {specialities.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
        <div className="text-sm text-gray-500 flex items-center px-2">
          {filteredTeachers.length} professeur(s) affiché(s) sur {teachers.length}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500">Chargement...</div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Matricule</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Nom & Prénom</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Téléphone</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Spécialité</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Contrat</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTeachers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-500">
                      {teachers.length === 0 ? 'Aucun professeur' : 'Aucun résultat'}
                    </td>
                  </tr>
                ) : (
                  filteredTeachers.map((t) => (
                    <tr key={t.id} className="border-t hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm font-mono">{t.matricule || '-'}</td>
                      <td className="py-3 px-4 font-medium">{t.name || '-'}</td>
                      <td className="py-3 px-4 text-sm">{t.email || '-'}</td>
                      <td className="py-3 px-4 text-sm">{t.phone || '-'}</td>
                      <td className="py-3 px-4 text-sm">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">{t.speciality || '-'}</span>
                      </td>
                      <td className="py-3 px-4 text-sm">{t.contract_type || '-'}</td>
                      <td className="py-3 px-4">
                        <button onClick={() => handleEdit(t)} className="text-blue-600 hover:text-blue-800 mr-3" title="Modifier">✏️</button>
                        <button onClick={() => handleDelete(t.id)} className="text-red-600 hover:text-red-800" title="Supprimer">🗑️</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <h2 className="text-xl font-bold text-[#2C3E50] mb-3">
                {editingTeacher ? '✏️ Modifier un professeur' : '➕ Créer un professeur'}
              </h2>

              <div className="flex items-center justify-between text-xs">
                {[
                  { n: 1, label: 'Infos perso', icon: '👤' },
                  { n: 2, label: 'Infos pro', icon: '💼' },
                  { n: 3, label: 'Affectations', icon: '🔗' },
                  { n: 4, label: 'Compte', icon: '🔑' },
                ].map((s, i) => (
                  <React.Fragment key={s.n}>
                    <div className="flex flex-col items-center flex-1">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= s.n ? 'bg-[#E67E22] text-white' : 'bg-gray-200 text-gray-500'}`}>
                        {step > s.n ? '✓' : s.n}
                      </div>
                      <span className={`mt-1 text-center ${step >= s.n ? 'text-[#E67E22] font-medium' : 'text-gray-400'}`}>
                        {s.icon} {s.label}
                      </span>
                    </div>
                    {i < 3 && (<div className={`h-0.5 flex-1 ${step > s.n ? 'bg-[#E67E22]' : 'bg-gray-200'}`} />)}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* ÉTAPE 1 */}
              {step === 1 && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                      <input type="text" placeholder="Diop" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                      <input type="text" placeholder="Mamadou" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                      <input type="date" value={formData.birth_date} onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sexe</label>
                      <select value={formData.sexe} onChange={(e) => setFormData({ ...formData, sexe: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]">
                        <option value="">-- Choisir --</option>
                        <option value="M">Masculin</option>
                        <option value="F">Féminin</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                    <input type="text" placeholder="77 123 45 67" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                    <input type="text" placeholder="Dakar, Sénégal" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]" />
                  </div>
                </>
              )}

              {/* ÉTAPE 2 */}
              {step === 2 && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Matricule <span className="text-xs text-gray-400 ml-1">(auto si vide)</span>
                      </label>
                      <input type="text" placeholder="Généré automatiquement" value={formData.matricule} onChange={(e) => setFormData({ ...formData, matricule: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:border-[#E67E22]" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Spécialité / Matière principale *</label>
                      <select value={formData.speciality} onChange={(e) => setFormData({ ...formData, speciality: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]">
                        <option value="">-- Choisir --</option>
                        {subjects.map((s) => (<option key={s.id} value={s.name}>{s.name}</option>))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Diplôme</label>
                    <input type="text" placeholder="Master, Licence, CAPES..." value={formData.diploma} onChange={(e) => setFormData({ ...formData, diploma: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date de recrutement</label>
                      <input type="date" value={formData.hire_date} onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type de contrat</label>
                      <select value={formData.contract_type} onChange={(e) => setFormData({ ...formData, contract_type: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]">
                        {CONTRACT_TYPES.map((c) => (<option key={c} value={c}>{c}</option>))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" checked={formData.is_active === true} onChange={() => setFormData({ ...formData, is_active: true })} className="accent-[#E67E22]" />
                        <span className="text-sm">● Actif</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" checked={formData.is_active === false} onChange={() => setFormData({ ...formData, is_active: false })} className="accent-[#E67E22]" />
                        <span className="text-sm">○ Inactif</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* ÉTAPE 3 */}
              {step === 3 && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 mb-2">
                    💡 Ajoutez les classes et matières que ce professeur enseignera.
                  </div>

                  {formData.assignments.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left py-2 px-3 font-semibold text-gray-600">Classe</th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-600">Matière</th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-600">Rôle</th>
                            <th className="text-center py-2 px-3 font-semibold text-gray-600">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.assignments.map((a, i) => (
                            <tr key={i} className="border-t">
                              <td className="py-2 px-3">{a.class_name}</td>
                              <td className="py-2 px-3">{a.subject_name}</td>
                              <td className="py-2 px-3">{a.role}</td>
                              <td className="py-2 px-3 text-center">
                                <button type="button" onClick={() => handleRemoveAssignment(i)} className="text-red-500 hover:text-red-700 text-lg" title="Supprimer">🗑️</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
                    <p className="text-sm font-medium text-gray-700 mb-3">➕ Ajouter une affectation</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Classe</label>
                        <select value={newAssignment.class_id} onChange={(e) => setNewAssignment({ ...newAssignment, class_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                          <option value="">-- Choisir --</option>
                          {classes.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Matière</label>
                        <select value={newAssignment.subject_id} onChange={(e) => setNewAssignment({ ...newAssignment, subject_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                          <option value="">-- Choisir --</option>
                          {subjects.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Rôle</label>
                        <select value={newAssignment.role} onChange={(e) => setNewAssignment({ ...newAssignment, role: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                          <option value="Enseignant">Enseignant</option>
                          <option value="Assistant">Assistant</option>
                          <option value="Coordinateur">Coordinateur</option>
                        </select>
                      </div>
                    </div>
                    <button type="button" onClick={handleAddAssignment} className="mt-3 bg-[#E67E22] text-white px-4 py-2 rounded-lg hover:bg-[#D35400] text-sm font-medium">
                      ➕ Ajouter
                    </button>
                  </div>

                  {/* PROFESSEUR PRINCIPAL - MULTI-SÉLECTION */}
                  <div className="border-t pt-4 mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      👑 Professeur principal de <span className="text-xs text-gray-400">(optionnel — plusieurs choix possibles)</span>
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-52 overflow-y-auto border border-gray-200 rounded-lg p-3">
                      {classes.length === 0 && (
                        <p className="text-gray-400 text-sm col-span-full">Aucune classe disponible</p>
                      )}
                      {classes.map((c) => {
                        const isChecked = formData.class_teacher_of.includes(String(c.id));
                        return (
                          <label
                            key={c.id}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition ${
                              isChecked
                                ? 'bg-[#F39C12]/10 border border-[#F39C12]'
                                : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const id = String(c.id);
                                setFormData({
                                  ...formData,
                                  class_teacher_of: e.target.checked
                                    ? [...formData.class_teacher_of, id]
                                    : formData.class_teacher_of.filter((x) => x !== id),
                                });
                              }}
                              className="accent-[#E67E22]"
                            />
                            <span className="text-sm font-medium">{c.name}</span>
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Cochez toutes les classes dont ce professeur sera le responsable principal.
                    </p>
                  </div>
                </>
              )}

              {/* ÉTAPE 4 */}
              {step === 4 && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 mb-2">
                    💡 Compte de connexion du professeur à SamaEcole
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email professionnel <span className="text-xs text-gray-400 ml-1">(auto si vide)</span>
                    </label>
                    <input type="email" placeholder="prof.diop@samaecole.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mot de passe temporaire <span className="text-xs text-gray-400 ml-1">(password123 par défaut)</span>
                      </label>
                      <input type="password" placeholder="••••••••" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe</label>
                      <input type="password" placeholder="••••••••" value={formData.password_confirmation} onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]" />
                    </div>
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.send_credentials} onChange={(e) => setFormData({ ...formData, send_credentials: e.target.checked })} className="accent-[#E67E22]" />
                    <span className="text-sm text-gray-700">☑ Envoyer les identifiants au professeur</span>
                  </label>

                  <div className="bg-gray-50 rounded-lg p-4 space-y-3 mt-4">
                    <h3 className="font-bold text-[#2C3E50] mb-2">📋 Récapitulatif</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-500">Nom :</span>
                      <span className="font-medium">{formData.lastName} {formData.firstName}</span>
                      <span className="text-gray-500">Téléphone :</span>
                      <span className="font-medium">{formData.phone || '-'}</span>
                      <span className="text-gray-500">Spécialité :</span>
                      <span className="font-medium">{formData.speciality || '-'}</span>
                      <span className="text-gray-500">Contrat :</span>
                      <span className="font-medium">{formData.contract_type}</span>
                      <span className="text-gray-500">Statut :</span>
                      <span className="font-medium">{formData.is_active ? '● Actif' : '○ Inactif'}</span>
                      <span className="text-gray-500">Email :</span>
                      <span className="font-medium">{formData.email || '(auto-généré)'}</span>
                    </div>

                    {formData.assignments.length > 0 && (
                      <div>
                        <p className="text-gray-500 text-sm mb-1">Affectations :</p>
                        <ul className="text-sm list-disc list-inside">
                          {formData.assignments.map((a, i) => (
                            <li key={i}><span className="font-medium">{a.class_name}</span> → {a.subject_name}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {formData.class_teacher_of.length > 0 && (
                      <div>
                        <p className="text-gray-500 text-sm mb-1">👑 Professeur principal de :</p>
                        <ul className="text-sm list-disc list-inside">
                          {formData.class_teacher_of.map((id) => {
                            const cls = classes.find((c) => String(c.id) === String(id));
                            return <li key={id} className="font-medium">{cls?.name || id}</li>;
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t flex justify-between items-center">
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700 text-sm">Annuler</button>

              <div className="flex gap-2">
                {step > 1 && (
                  <button onClick={() => setStep(step - 1)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">← Retour</button>
                )}

                {step < 4 ? (
                  <button onClick={() => setStep(step + 1)} disabled={!canGoNext()} className="px-4 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium">Suivant →</button>
                ) : (
                  <button onClick={handleSubmit} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">✅ {editingTeacher ? 'Enregistrer' : 'Créer le professeur'}</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showCredentialsModal && createdCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b bg-green-50 rounded-t-2xl">
              <h2 className="text-xl font-bold text-green-700">✅ Professeur créé avec succès !</h2>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-purple-900 mb-3">👨‍🏫 IDENTIFIANTS PROFESSEUR</p>
                <div className="space-y-2 text-sm">
                  <div><span className="text-gray-500">Nom :</span><span className="font-medium ml-2">{createdCredentials.name}</span></div>
                  {createdCredentials.matricule && (<div><span className="text-gray-500">Matricule :</span><span className="font-mono font-medium ml-2">{createdCredentials.matricule}</span></div>)}
                  {createdCredentials.speciality && (<div><span className="text-gray-500">Spécialité :</span><span className="font-medium ml-2">{createdCredentials.speciality}</span></div>)}
                  {createdCredentials.contract_type && (<div><span className="text-gray-500">Contrat :</span><span className="font-medium ml-2">{createdCredentials.contract_type}</span></div>)}
                  {createdCredentials.assignments_count > 0 && (<div><span className="text-gray-500">Affectations :</span><span className="font-medium ml-2">{createdCredentials.assignments_count} cours</span></div>)}
                  <div>
                    <span className="text-gray-500">📧 Email :</span>
                    <div className="font-mono text-xs bg-white px-2 py-1 rounded border mt-1 break-all">{createdCredentials.email}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">🔑 Mot de passe :</span>
                    <div className="font-mono text-sm bg-white px-2 py-1 rounded border mt-1">{createdCredentials.password}</div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 px-3 py-2 rounded-lg text-xs">
                ⚠️ Le professeur devra changer ce mot de passe à sa 1ère connexion.
              </div>

              <div className="flex gap-2">
                <button type="button" onClick={handleCopy} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm">📋 Copier</button>
                <button type="button" onClick={handlePrint} className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-sm">🖨️ Imprimer</button>
                <button type="button" onClick={() => { setShowCredentialsModal(false); setCreatedCredentials(null); closeModal(); }} className="flex-1 px-4 py-2 bg-[#E67E22] hover:bg-[#D35400] text-white rounded-lg font-medium text-sm">✖ Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Teachers;